// Contracts — javelin manager cabinet reads (2026-06-11 manager QA).
// Worker side: apps/api/src/javelin/economics.ts managerOverview() and
// managerSalaryCalc(). Client side: apps/internal javelin island
// (OverviewScreen / FinanceScreen).
import { z } from 'zod';

// GET /api/javelin/managerOverview?tab=tracker|mods&iteration=&mod_id=
// mod_id (mods view only) drills the whole overview down to ONE
// moderator; the source rule is unchanged: accepted/pending/rejected
// read tracker_status (Tracker truth), cancelled matches the mod-page
// status column. trend_iteration = daily session counts over the WHOLE
// current selection from its first session day (trend_start) to today.
export const JavManagerOverviewQuerySchema = z.object({
  tab: z.enum(['tracker', 'mods']).optional(),
  iteration: z.string().optional(),
  mod_id: z.string().optional(),
});

export const JavManagerOverviewResSchema = z.looseObject({
  tab: z.enum(['tracker', 'mods']),
  mod_id: z.string().default(''),
  total: z.number(),
  accepted: z.number(),
  pending: z.number(),
  rejected: z.number(),
  cancelled: z.number(),
  today: z.number(),
  week: z.number(),
  active_mods: z.number(),
  open_homes: z.number(),
  closed_homes: z.number(),
  issued: z.number(),
  spent: z.number(),
  balance: z.number(),
  skin_tone_distribution: z.array(z.looseObject({
    code: z.string(), count: z.number(), target: z.number(),
  })),
  gender_distribution: z.looseObject({
    male: z.number(), female: z.number(), other: z.number(),
    pct_male: z.number(), pct_female: z.number(), pct_other: z.number(),
  }),
  age_distribution: z.looseObject({
    a18_35: z.number(), a36_54: z.number(), a55_70: z.number(),
    pct_18_35: z.number(), pct_36_54: z.number(), pct_55_70: z.number(),
  }),
  trend_iteration: z.array(z.number()).default([]),
  trend_start: z.string().default(''),
});

export type JavManagerOverviewRes = z.infer<typeof JavManagerOverviewResSchema>;

// GET /api/javelin/managerSalaryCalc — D1-computed «К оплате»
// (salary_calc.ts formula). to_pay_usd may be negative — real debt.
// rejected_paid_or_invoiced (I4): how many of the mod's rejected
// sessions carry a Paid/Invoiced marker in the tracker sheet's Payment
// column — each one claws back `rate_usd`. clawback_available=false
// means the tracker sheet was unreachable/empty while rejected rows
// exist: the counts are 0 NOT because nothing is owed, but because the
// marker couldn't be read — the UI must say so, not show «0».
export const JavSalaryCalcResSchema = z.looseObject({
  iteration: z.string(),
  rate_usd: z.number(),
  clawback_available: z.boolean(),
  iteration_closed: z.boolean().default(false),
  closed_at: z.string().default(''),
  rows: z.array(z.looseObject({
    mod_id: z.coerce.string(),
    accepted: z.number(),
    pending: z.number(),
    rejected: z.number(),
    cancelled: z.number().default(0),
    kpi: z.number().default(0),
    captured: z.number().default(0),
    bonus_usd: z.number().default(0),
    /** Σ базы за accepted+pending ($50/$60 посессионно) — не считать на клиенте. */
    earned_usd: z.number().default(0),
    rejected_paid_or_invoiced: z.number(),
    paid_before_usd: z.number(),
    to_pay_usd: z.number(),
  })),
  total_to_pay_usd: z.number(),
  /** ADR-0049: кто под зарплатным хабом (PM сворачивает эти строки) и
   *  внешняя ставка base+markup. Суммы хаба — в adminKassa.salary_hub. */
  hub: z.looseObject({
    sub_mod_ids: z.array(z.coerce.string()),
    markup_usd: z.number(),
    rate_usd: z.number(),
  }).optional(),
});

export type JavSalaryCalcRes = z.infer<typeof JavSalaryCalcResSchema>;

// «Проект завершён» — MANUAL iteration close (managerFinalizeProject).
// iteration omitted → the worker closes CURRENT_JAVELIN_ITERATION; an
// explicit key lets the manager close a PAST wave after CURRENT moved on.
export const JavFinalizeProjectReqSchema = z.object({
  iteration: z.string().optional(),
});
export type JavFinalizeProjectReq = z.infer<typeof JavFinalizeProjectReqSchema>;

// «Проект завершён» — iteration close result (managerFinalizeProject).
export const JavFinalizeProjectResSchema = z.looseObject({
  ok: z.boolean(),
  iteration: z.string(),
  mod_count: z.number(),
  total_bonus_usd: z.number(),
  rows: z.array(z.looseObject({
    mod_id: z.coerce.string(),
    kpi: z.number(),
    captured: z.number(),
    accepted: z.number(),
    bonus_usd: z.number(),
  })),
});

export type JavFinalizeProjectRes = z.infer<typeof JavFinalizeProjectResSchema>;

// Finance write-ops (2026-06-20) — per-moderator budget/salary detail edits.
// Worker: economics.ts managerBudgetEdit / managerSalaryPaymentEdit /
// managerSalaryPaymentDelete. Client: apps/internal FinanceDetails.
export const JavBudgetEditReqSchema = z.object({
  tx_id: z.string().min(1),
  amount_usd: z.number().positive().max(10000),
});
export type JavBudgetEditReq = z.infer<typeof JavBudgetEditReqSchema>;

export const JavSalaryPaymentEditReqSchema = z.object({
  tx_id: z.string().min(1),
  amount_usd: z.number().positive().max(10000),
});
export type JavSalaryPaymentEditReq = z.infer<typeof JavSalaryPaymentEditReqSchema>;

export const JavSalaryPaymentDeleteReqSchema = z.object({
  tx_id: z.string().min(1),
});
export type JavSalaryPaymentDeleteReq = z.infer<typeof JavSalaryPaymentDeleteReqSchema>;

export const JavFinMutationResSchema = z.looseObject({
  ok: z.boolean(),
  idempotent_replay: z.boolean().default(false),
});
export type JavFinMutationRes = z.infer<typeof JavFinMutationResSchema>;

// ── Desktop manager: Locations page (read) ──────────────────────────────────
// GET /api/javelin/managerLocations?iteration=&mod_id= — location setup photos
// grouped by LA day. One card per home (locations.home_id is unique). Worker:
// economics.ts managerLocations. Client: apps/internal desktop (handoff TBD).
export const JavManagerLocationsQuerySchema = z.object({
  iteration: z.string().optional(),
  mod_id: z.string().optional(),
});

export const JavLocationCardSchema = z.looseObject({
  home_id: z.string(),
  mod_id: z.coerce.string(),
  mod_name: z.string().default(''),
  photo_key: z.string().default(''),
  status: z.string().default(''),
  filled: z.number().default(0),
  max_slots: z.number().default(5),
  created_at: z.string().default(''),
});

export const JavManagerLocationsResSchema = z.looseObject({
  iteration: z.string(),
  days: z.array(z.looseObject({
    day: z.string(),                 // 'YYYY-MM-DD' (LA)
    count: z.number(),
    cards: z.array(JavLocationCardSchema),
  })),
});
export type JavManagerLocationsRes = z.infer<typeof JavManagerLocationsResSchema>;

// ── Desktop manager: notify moderators (Web Push) ───────────────────────────
// POST /api/javelin/managerNotifyModerators — a PM pushes a free-text message
// to selected moderators' subscribed devices. Worker: push.ts. Delivery needs
// the moderator linked to an account (moderators.user_id) AND a push
// subscription; mods without either are reported with sent:0.
export const JavNotifyModeratorsReqSchema = z.object({
  mod_ids: z.array(z.string().min(1)).min(1),
  message: z.string().trim().min(1).max(280),
  title: z.string().trim().max(80).optional(),
});

export const JavNotifyModeratorsResSchema = z.looseObject({
  ok: z.boolean(),
  sent: z.number(),
  recipients: z.array(z.looseObject({
    mod_id: z.coerce.string(),
    devices: z.number(),
    sent: z.number(),
  })),
});
export type JavNotifyModeratorsRes = z.infer<typeof JavNotifyModeratorsResSchema>;

// ── Manager reads touched by the javelin_admin read-only scope (2026-07-24).
// Shapes snapped from the worker response literals (economics.ts) — the
// admin role reuses these endpoints filtered to the caller's subordinates,
// so pm and admin share ONE contract per route.

// GET /api/javelin/managerModerators?iteration=
export const JavManagerModRowSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  status: z.enum(['active', 'inactive']),
  today: z.number(),
  week: z.number(),
  accepted: z.number(),
  pending: z.number(),
  rejected: z.number(),
  cancelled: z.number(),
  captured: z.number(),
  uploaded: z.number(),
  kpi: z.number(),
  issued: z.number(),
  paid_out: z.number(),
  balance: z.number(),
  bonus: z.number(),
  spent_calc: z.number(),
  earned_calc: z.number(),
  kpi_bonus_calc: z.number(),
  homes_open: z.number(),
  homes_closed: z.number(),
  home: z.string().nullable(),
  current_pid: z.string().nullable(),
  last_min: z.number().nullable(),
  live: z.boolean(),
});
export type JavManagerModRow = z.infer<typeof JavManagerModRowSchema>;

export const JavManagerModeratorsQuerySchema = z.object({
  iteration: z.string().optional(),
});
/** Общая админская касса — ОТДЕЛЬНЫЙ счёт, один на обеих админов. Никогда
 *  не складывается с личным бюджетом человека: это разные деньги с разным
 *  назначением (migration 0035). PM пополняет её отдельной строкой в списке. */
export const JavAdminCabinetSchema = z.looseObject({
  mod_id: z.string(),
  name: z.string(),
  in_from_pm_usd: z.number(),
  delegated_down_usd: z.number(),
  /** Забранное админами себе: разница со съёмок + надбавка за менеджмент.
   *  Для PM это такой же расход кабинета, как выдача моду (владелец 05.09). */
  kassa_taken_usd: z.number().optional(),
  capture_usd: z.number().optional(),
  markup_usd: z.number().optional(),
  /** Надбавка сессий, чей расчётный период ещё не закрыт. */
  markup_pending_usd: z.number().optional(),
  /** Роздано + забранное себе. Именно это показывается как «потрачено». */
  spent_total_usd: z.number().optional(),
  balance_usd: z.number(),
});
export type JavAdminCabinet = z.infer<typeof JavAdminCabinetSchema>;

export const JavManagerModeratorsResSchema = z.looseObject({
  rows: z.array(JavManagerModRowSchema),
  admin_cabinet: JavAdminCabinetSchema.optional(),
  /** Остаток кассы — то же число, что и `admin_cabinet.balance_usd`
   *  (совместимость со старым клиентом). */
  admin_balance_usd: z.number().optional(),
  /** Личный операционный бюджет админа на собственные съёмки. */
  own_balance_usd: z.number().optional(),
  /** mod_id самого админа — адресат самопополнения (adminBudgetIssue). */
  own_mod_id: z.string().optional(),
});
export type JavManagerModeratorsRes = z.infer<typeof JavManagerModeratorsResSchema>;

// GET /api/javelin/managerModeratorDetail?mod_id=&iteration=
export const JavManagerModeratorDetailQuerySchema = z.object({
  mod_id: z.string().min(1),
  iteration: z.string().optional(),
});
export const JavManagerModeratorDetailResSchema = JavManagerModRowSchema.extend({
  recent_participants: z.array(z.looseObject({
    participant_id: z.string(),
    name: z.string(),
    status: z.string(),
    home_id: z.string(),
    intake_at: z.string(),
    skin_tone: z.string(),
    gender: z.string(),
    age: z.string(),
  })),
  homes: z.array(z.looseObject({
    home_id: z.string(),
    status: z.string(),
    opened_at: z.string(),
    closed_at: z.string(),
    n_participants: z.number(),
  })),
  recent_transactions: z.array(z.looseObject({
    id: z.string(),
    ts: z.string(),
    kind: z.string(),
    direction: z.string(),
    amount: z.number(),
    note: z.string(),
    counterparty_mod_id: z.string().optional(),
  })),
  planned_today_count: z.number(),
});
export type JavManagerModeratorDetailRes = z.infer<typeof JavManagerModeratorDetailResSchema>;

// PM cascade block on managerFinance (pm-only; absent for admins).
// Worker: apps/api/src/javelin/delegation.ts buildCascade().
export const JavCascadeSchema = z.looseObject({
  issued_total_usd: z.number(),
  /** Общая касса — одна на обеих админов (migration 0035). */
  cabinet: z.looseObject({
    in_from_pm: z.number(),
    delegated_down: z.number(),
    balance: z.number(),
  }),
  admins: z.array(z.looseObject({
    mod_id: z.string(),
    name: z.string(),
    /** Сколько ЭТОТ человек выдал с общей кассы. */
    delegated_down: z.number(),
    /** Личный бюджет на свои съёмки; к кассе отношения не имеет. */
    own_balance: z.number(),
  })),
  mods: z.array(z.looseObject({
    mod_id: z.string(),
    name: z.string(),
    received_from_admin: z.number(),
    issued_direct: z.number(),
    spent_calc: z.number(),
    balance: z.number(),
  })),
});
export type JavCascade = z.infer<typeof JavCascadeSchema>;

// GET /api/javelin/managerFinance
export const JavManagerFinanceResSchema = z.looseObject({
  cascade: JavCascadeSchema.optional(),
  total_issued: z.number(),
  total_spent: z.number(),
  total_balance: z.number(),
  total_bonus: z.number(),
  per_mod: z.array(z.looseObject({
    mod_id: z.string(),
    name: z.string(),
    issued: z.number(),
    paid_out: z.number(),
    balance: z.number(),
    bonus: z.number(),
  })),
  recent_transactions: z.array(z.looseObject({
    ts: z.string(),
    mod_id: z.string(),
    mod_name: z.string(),
    kind: z.string(),
    direction: z.string(),
    amount: z.number(),
    note: z.string(),
  })),
  planned_payouts: z.array(z.looseObject({
    mod_id: z.string(),
    mod_name: z.string(),
    scheduled_iso: z.string(),
    amount: z.number(),
    note: z.string(),
  })),
});
export type JavManagerFinanceRes = z.infer<typeof JavManagerFinanceResSchema>;

// GET /api/javelin/managerSalaryPayments
export const JavManagerSalaryPaymentsResSchema = z.looseObject({
  payments: z.array(z.looseObject({
    id: z.string(),
    mod_id: z.string(),
    mod_name: z.string(),
    ts: z.string(),
    amount: z.number(),
    note: z.string(),
    status: z.string(),
  })),
  total_usd: z.number(),
  count: z.number(),
});
export type JavManagerSalaryPaymentsRes = z.infer<typeof JavManagerSalaryPaymentsResSchema>;

// GET /api/javelin/managerSheetsBundle — one fetch for Overview hero +
// Moderators list (mod_personal_sheet_stats.getSheetsBundle).
export const JavModSheetsStatsSchema = z.looseObject({
  total: z.number(),
  week: z.number(),
  accepted: z.number(),
  pending: z.number(),
  rejected: z.number(),
  cancelled: z.number(),
  skin_counts: z.record(z.string(), z.number()),
  gender_counts: z.looseObject({ male: z.number(), female: z.number(), other: z.number() }),
  age_counts: z.looseObject({ a18_35: z.number(), a36_54: z.number(), a55_70: z.number() }),
  mods_counted: z.number(),
  mods_missing: z.number(),
});
export const JavManagerSheetsBundleResSchema = z.looseObject({
  total: JavModSheetsStatsSchema,
  per_mod: z.array(z.looseObject({
    mod_id: z.string(),
    today: z.number(),
    week: z.number(),
    total: z.number(),
    captured: z.number(),
    uploaded: z.number(),
    accepted: z.number(),
    pending: z.number(),
    rejected: z.number(),
    cancelled: z.number(),
  })),
});
export type JavManagerSheetsBundleRes = z.infer<typeof JavManagerSheetsBundleResSchema>;

// GET /api/javelin/managerModeratorModSheetsStats?mod_id=
export const JavManagerModSheetsStatsQuerySchema = z.object({
  mod_id: z.string().min(1),
});

// ── Admin budget delegation (2026-07-25) ────────────────────────────────────
// Worker: apps/api/src/javelin/delegation.ts. An admin (javelin_admin) hands
// budget down to their own subordinate from their own balance; a transfer is
// a pair of budget_issued rows (in on the sub / out on the admin) linked by
// counterparty_mod_id. Overdraw refused (insufficient_funds).
export const JavAdminBudgetIssueReqSchema = z.object({
  mod_id: z.string().min(1),
  amount_usd: z.number().positive(),
  note: z.string().max(500).optional(),
  idempotency_key: z.string().max(80).optional(),
});
export type JavAdminBudgetIssueReq = z.infer<typeof JavAdminBudgetIssueReqSchema>;

export const JavAdminBudgetIssueResSchema = z.looseObject({
  ok: z.boolean(),
  idempotent_replay: z.boolean().optional(),
  admin_balance_usd: z.number(),
});
export type JavAdminBudgetIssueRes = z.infer<typeof JavAdminBudgetIssueResSchema>;

export const JavAdminBudgetDeleteReqSchema = z.object({
  tx_id: z.string().min(1),
});
export type JavAdminBudgetDeleteReq = z.infer<typeof JavAdminBudgetDeleteReqSchema>;


// ── Касса админ-кабинета (ADR-0049, migration 0038) ─────────────────────────
// Worker: apps/api/src/javelin/kassa.ts. Касса = derived-разметка баланса
// кабинета: захватные начисления (participants.kassa_cents по не-отклонённым
// сессиям) + рисковая доля зарплатного потока. Фонды версионные
// (javelin_fund_rate); водопад хаба: пул $50×сессии закрывается первым.

// GET /api/javelin/adminKassa
export const JavKassaFundSchema = z.looseObject({
  key: z.string(),
  usd: z.number(),
});

export const JavHubSalaryStateSchema = z.looseObject({
  payable_sessions: z.number(),
  base_pay_usd: z.number(),
  markup_usd: z.number(),
  rate_usd: z.number(),
  owed_usd: z.number(),
  received_usd: z.number(),
  to_pay_usd: z.number(),
  pool_required_usd: z.number(),
  pool_in_usd: z.number(),
  pool_paid_usd: z.number(),
  pool_balance_usd: z.number(),
  risk_from_salary_usd: z.number(),
});
export type JavHubSalaryState = z.infer<typeof JavHubSalaryStateSchema>;

export const JavAdminKassaResSchema = z.looseObject({
  base_usd: z.number(),
  /** base − действующая ставка активных подчинённых (null — нет активных);
   *  для подписи «+$X за сессию» — среднее по факту начислений врёт на
   *  легаси-сессиях. */
  per_session_now_usd: z.number().nullable().default(null),
  cabinet: z.looseObject({
    in_from_pm_usd: z.number(),
    delegated_down_usd: z.number(),
    balance_usd: z.number(),
    /** Свободно = баланс кабинета − захватная касса (ADR-0049 §инварианты). */
    free_usd: z.number(),
  }),
  kassa: z.looseObject({
    total_usd: z.number(),
    capture_usd: z.number(),
    /** Уже выведено админам как зарплата (kind='kassa_payout'). */
    paid_out_usd: z.number().optional(),
    /** Заработано минус выведено — столько ещё можно забрать. */
    left_usd: z.number().optional(),
    /** Надбавка $20 за зарплатные сессии подчинённых — заработана, но до
     *  05.09 показывалась только через зарплатный хаб, то есть нигде. */
    markup_usd: z.number().optional(),
    markup_sessions: z.number().optional(),
    markup_pending_usd: z.number().optional(),
    sessions: z.number(),
    funds: z.array(JavKassaFundSchema),
    /** Остаток захватного потока; может быть отрицательным — показывать явно. */
    residual_usd: z.number(),
  }),
  salary_hub: JavHubSalaryStateSchema,
  /** Дополнительные начисления модам без админской надстройки (migration 0051):
   *  «база заказчика − ставка мода». Это НЕ касса — в total_usd кассы, в
   *  фонды и в «свободно» кабинета не входит. */
  extra: z.looseObject({
    total_usd: z.number(),
    sessions: z.number(),
    by_mod: z.array(z.looseObject({
      mod_id: z.coerce.string(),
      name: z.string(),
      sessions: z.number(),
      usd: z.number(),
      per_session_now_usd: z.number().nullable().default(null),
    })),
  }).default({ total_usd: 0, sessions: 0, by_mod: [] }),
  by_mod: z.array(z.looseObject({
    mod_id: z.coerce.string(),
    name: z.string(),
    sessions: z.number(),
    kassa_usd: z.number(),
  })),
  recent: z.array(z.looseObject({
    participant_id: z.string(),
    session_id: z.string(),
    session_date: z.string(),
    mod_id: z.coerce.string(),
    mod_name: z.string(),
    kassa_usd: z.number(),
  })),
});
export type JavAdminKassaRes = z.infer<typeof JavAdminKassaResSchema>;

// POST /api/javelin/hubSalaryPay — PM платит хабу (kind='salary_hub').
export const JavHubSalaryPayReqSchema = z.object({
  amount_usd: z.number().positive().max(50000),
  note: z.string().max(500).optional(),
  idempotency_key: z.string().max(80).optional(),
});
export type JavHubSalaryPayReq = z.infer<typeof JavHubSalaryPayReqSchema>;

/**
 * Вывод производственной кассы админам как зарплаты.
 *
 * Заработанное админами (разница со съёмок + надбавка за менеджмент) до сих
 * пор было только расчётной величиной: посчитать её было можно, а забрать —
 * нечем, эндпоинта не существовало. Первые выводы уже произошли и легли мимо
 * системы, поэтому проводка нужна и задним числом (`ts`).
 *
 * Кому: только 16 и 17 — касса общая на двоих, других получателей у неё нет.
 */
export const JavKassaPayoutReqSchema = z.object({
  mod_id: z.string().trim().min(1),
  amount_usd: z.number().positive().max(50000),
  /** Дата проводки задним числом, ISO YYYY-MM-DD. Пусто — сегодня. */
  ts_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  note: z.string().max(500).optional(),
  idempotency_key: z.string().max(80).optional(),
});
export type JavKassaPayoutReq = z.infer<typeof JavKassaPayoutReqSchema>;

export const JavKassaPayoutResSchema = z.looseObject({
  ok: z.boolean(),
  mod_id: z.string().optional(),
  amount_usd: z.number().optional(),
  /** Сколько осталось в производственной кассе после этого вывода. */
  kassa_left_usd: z.number().optional(),
  idempotent_replay: z.boolean().optional(),
});
export type JavKassaPayoutRes = z.infer<typeof JavKassaPayoutResSchema>;

// ── S3-watch (2026-07-25) ───────────────────────────────────────────────────
// Worker: apps/api/src/javelin/s3_watch.ts. Hourly poller over the client
// delivery bucket; audit rows = added/deleted/modified diffs. Admin scope
// sees only rows attributed to their subordinates.
export const JavS3AuditQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(500).optional(),
  event: z.enum(['added', 'deleted', 'modified']).optional(),
});

export const JavS3AuditResSchema = z.looseObject({
  rows: z.array(z.looseObject({
    id: z.string(),
    ts: z.string(),
    event: z.string(),
    key: z.string(),
    size: z.number(),
    pid: z.string(),
    mod_id: z.string(),
    batch: z.string(),
    notified: z.string(),
  })),
  snapshot_total: z.number().optional(),
  snapshot_bytes: z.number().optional(),
});
export type JavS3AuditRes = z.infer<typeof JavS3AuditResSchema>;

